import { InjectionToken } from '@angular/core';

import {
  ChangeEmailDialogData,
  ChangeEmailDialogOutput,
  ChangePasswordDialogOutput,
  CompanyConfigureAuthenticatorDialogInput,
  CompanyConfigureAuthenticatorDialogOutput,
  ConfirmEmailDialogInput,
  ConfirmEmailDialogOutput,
  ConfirmPasswordDialogInput,
  ConfirmPasswordDialogOutput,
  ConfirmSmsDialogInput,
  ConfirmSmsDialogOutput,
  EnterPhoneNumberDialogOutput,
  ForgotPasswordDialogInput,
  ForgotPasswordDialogOutput,
  ForgotPasswordSubmitDialogInput,
  ForgotPasswordSubmitDialogOutput,
  TempPasswordDialogOutput,
  TotpChangeAuthAppDialogOutput,
  TotpQrCodeDialogInput,
  TotpQrCodeDialogOutput,
  TotpRemoveAuthenticatorDialogOutput,
  ValidateCredentialsDialogInput,
  ValidateCredentialsDialogOutput,
} from '../app-global/auth/dialogs/auth-dialog.contracts';

import { DialogOpener, noopDialogRef } from './dialog-opener.type';

/**
 * Dialog-opener tokens for the auth dialog components (DEV-26105 T6, Option B). The concrete
 * components live in `shared/ui/core` (`@ninety/ui/core`); importing them
 * here would reopen the `platform/state -> shared/ui/core` edge that this epic exists to sever. Each
 * token defaults to a no-op opener and is bound at the app composition root via
 * `{ provide: X_DIALOG, useFactory: (d: MatDialog) => (cfg) => d.open(XComponent, cfg), deps: [MatDialog] }`.
 */

export const CHANGE_EMAIL_DIALOG = new InjectionToken<DialogOpener<ChangeEmailDialogData, ChangeEmailDialogOutput>>(
  'CHANGE_EMAIL_DIALOG',
  {
    providedIn: 'root',
    factory: (): DialogOpener<ChangeEmailDialogData, ChangeEmailDialogOutput> => () => noopDialogRef(),
  }
);

export const CHANGE_PASSWORD_DIALOG = new InjectionToken<DialogOpener<unknown, ChangePasswordDialogOutput>>(
  'CHANGE_PASSWORD_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<unknown, ChangePasswordDialogOutput> => () => noopDialogRef() }
);

export const CONFIRM_EMAIL_DIALOG = new InjectionToken<DialogOpener<ConfirmEmailDialogInput, ConfirmEmailDialogOutput>>(
  'CONFIRM_EMAIL_DIALOG',
  {
    providedIn: 'root',
    factory: (): DialogOpener<ConfirmEmailDialogInput, ConfirmEmailDialogOutput> => () => noopDialogRef(),
  }
);

export const CONFIRM_SMS_DIALOG = new InjectionToken<DialogOpener<ConfirmSmsDialogInput, ConfirmSmsDialogOutput>>(
  'CONFIRM_SMS_DIALOG',
  {
    providedIn: 'root',
    factory: (): DialogOpener<ConfirmSmsDialogInput, ConfirmSmsDialogOutput> => () => noopDialogRef(),
  }
);

export const ENTER_PHONE_NUMBER_DIALOG = new InjectionToken<DialogOpener<unknown, EnterPhoneNumberDialogOutput>>(
  'ENTER_PHONE_NUMBER_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<unknown, EnterPhoneNumberDialogOutput> => () => noopDialogRef() }
);

export const FORGOT_PASSWORD_DIALOG = new InjectionToken<
  DialogOpener<ForgotPasswordDialogInput, ForgotPasswordDialogOutput>
>('FORGOT_PASSWORD_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<ForgotPasswordDialogInput, ForgotPasswordDialogOutput> => () => noopDialogRef(),
});

export const FORGOT_PASSWORD_SUBMIT_DIALOG = new InjectionToken<
  DialogOpener<ForgotPasswordSubmitDialogInput, ForgotPasswordSubmitDialogOutput>
>('FORGOT_PASSWORD_SUBMIT_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<ForgotPasswordSubmitDialogInput, ForgotPasswordSubmitDialogOutput> => () => noopDialogRef(),
});

export const TEMP_PASSWORD_DIALOG = new InjectionToken<DialogOpener<unknown, TempPasswordDialogOutput>>(
  'TEMP_PASSWORD_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<unknown, TempPasswordDialogOutput> => () => noopDialogRef() }
);

export const TOTP_QR_CODE_DIALOG = new InjectionToken<DialogOpener<TotpQrCodeDialogInput, TotpQrCodeDialogOutput>>(
  'TOTP_QR_CODE_DIALOG',
  {
    providedIn: 'root',
    factory: (): DialogOpener<TotpQrCodeDialogInput, TotpQrCodeDialogOutput> => () => noopDialogRef(),
  }
);

export const TOTP_CHANGE_AUTH_APP_DIALOG = new InjectionToken<DialogOpener<unknown, TotpChangeAuthAppDialogOutput>>(
  'TOTP_CHANGE_AUTH_APP_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<unknown, TotpChangeAuthAppDialogOutput> => () => noopDialogRef() }
);

export const TOTP_REMOVE_AUTHENTICATOR_DIALOG = new InjectionToken<
  DialogOpener<unknown, TotpRemoveAuthenticatorDialogOutput>
>('TOTP_REMOVE_AUTHENTICATOR_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<unknown, TotpRemoveAuthenticatorDialogOutput> => () => noopDialogRef(),
});

export const COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG = new InjectionToken<
  DialogOpener<CompanyConfigureAuthenticatorDialogInput, CompanyConfigureAuthenticatorDialogOutput>
>('COMPANY_CONFIGURE_AUTHENTICATOR_DIALOG', {
  providedIn: 'root',
  factory:
    (): DialogOpener<CompanyConfigureAuthenticatorDialogInput, CompanyConfigureAuthenticatorDialogOutput> => () =>
      noopDialogRef(),
});

export const VALIDATE_CREDENTIALS_DIALOG = new InjectionToken<
  DialogOpener<ValidateCredentialsDialogInput, ValidateCredentialsDialogOutput>
>('VALIDATE_CREDENTIALS_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<ValidateCredentialsDialogInput, ValidateCredentialsDialogOutput> => () => noopDialogRef(),
});

export const VERIFY_PASSWORD_DIALOG = new InjectionToken<
  DialogOpener<ConfirmPasswordDialogInput, ConfirmPasswordDialogOutput>
>('VERIFY_PASSWORD_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<ConfirmPasswordDialogInput, ConfirmPasswordDialogOutput> => () => noopDialogRef(),
});
